/**
 * Supabase client — singleton instance shared across the app.
 *
 * Uses the MODERN publishable key architecture (sb_publishable_...).
 * No secret credential is ever bundled with browser code.
 *
 * The client auto-refreshes access tokens (default 1hr expiry) via
 * a rotating refresh token; consumers never touch expiry directly —
 * they subscribe to onAuthStateChange for state updates.
 */
import { createClient } from "@supabase/supabase-js";

const url = process.env.REACT_APP_SUPABASE_URL;
const publishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!url || !publishableKey) {
    // Surface config errors loudly during dev; the AuthGate renders a
    // friendly message if this crash would otherwise take down the app.
    console.error(
        "[qoryn/auth] Missing REACT_APP_SUPABASE_URL or REACT_APP_SUPABASE_PUBLISHABLE_KEY. " +
        "Auth will not function until frontend/.env is populated.",
    );
}

export const supabase = createClient(url ?? "", publishableKey ?? "", {
    auth: {
        // Persist session in localStorage; Supabase manages refresh timing.
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // storageKey is scoped to this project so multiple Supabase apps
        // could coexist on the same origin without colliding.
        storageKey: "qoryn.auth.v1",
    },
});

/**
 * Ergonomic helper to fetch the current access token for backend calls.
 * Returns null if the user is not signed in.
 */
export async function getAccessToken() {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token ?? null;
}
